import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Sparkles, ArrowUpRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useEnforcement } from '@/hooks/useEnforcement';
import { getCurrency } from '@/lib/checkout';
import { formatPlanPrice, formatProductsPhrase, isCapTier, planDisplayName } from '@/lib/plans';
import { PlanPriceCard } from './PlanPriceCard';

export interface QuotaCapInfo {
  /** How many products the user tried to optimize (the button count). */
  requested: number;
  /** How many actually fit in the remaining plan quota (limit - used). */
  remaining: number;
  /** The plan's optimization limit. */
  limit: number;
  /** How many optimizations were already used. */
  used: number;
}

interface QuotaLimitDialogProps {
  open: boolean;
  info: QuotaCapInfo | null;
  onClose: () => void;
  /** Proceed with optimizing only the `remaining` products that fit. */
  onProceed: () => void;
}

/**
 * Shown BEFORE an optimization starts when the user selected more products than
 * fit in their remaining plan quota (e.g. 90 selected, only 30 left on free).
 * Without this the server silently trims 90 → 30 and the user never learns 60
 * were dropped. Lets the user upgrade first (so they don't burn their last slots
 * on an arbitrary subset) or knowingly run just the ones that fit.
 */
export function QuotaLimitDialog({ open, info, onClose, onProceed }: QuotaLimitDialogProps) {
  const { t, i18n } = useTranslation('enforcement');
  const navigate = useNavigate();
  // C-9: the dialog didn't know which plan to point at — it's on the same
  // page as other enforcement UI, so the cached useEnforcement() query is free.
  const { recommendedPlan, nextPlan } = useEnforcement();
  const effectivePlan = recommendedPlan || nextPlan;
  const handleOpenChange = (v: boolean) => { if (!v) onClose(); };

  if (!info) return null;

  // How many of the requested products get silently skipped if the user
  // proceeds without upgrading — named explicitly instead of only saying
  // "upgrade to do more".
  const dropped = info.requested - info.remaining;

  // Gleichstand (Bericht 8.3 Punkt 8): requested == remaining. Nichts wird
  // gedroppt, aber der Lauf verbraucht das GESAMTE Rest-Kontingent — die
  // Ueber-Limit-Copy ("nur X von Y") waere hier schlicht falsch.
  const isExactFit = dropped === 0;

  // Review-Auflage: der Platzhalter hiess `count`. Das ist bei i18next ein
  // Reservename und schaltet die Pluralisierung ein — ohne vollstaendiges
  // `_one`/`_other`-Set faellt i18next auf den Basisschluessel zurueck, und der
  // nennt die Mehrzahl. Bei genau einem freien Slot (erreichbar: das Gate ist
  // `remaining > 0 && requested >= remaining`) stand dort "1 Produkte".
  // Deshalb ein freier Platzhaltername plus eine eigene Einzahl-Copy statt
  // halber Plural-Maschinerie. Jeder Schluessel bekommt nur die Werte, die er
  // auch nennt (`used` war toter Ballast).
  const exactDescription = info.remaining === 1
    ? t('quotaCap.descriptionExactSingle', { limit: info.limit })
    : t('quotaCap.descriptionExact', { products: info.remaining, limit: info.limit });

  const currency = getCurrency();
  const price = effectivePlan ? formatPlanPrice(effectivePlan, currency, 'monthly') : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            {isExactFit ? t('quotaCap.titleExact') : t('quotaCap.title')}
          </DialogTitle>
          <DialogDescription>
            {isExactFit
              ? exactDescription
              : t('quotaCap.description', {
                  requested: info.requested,
                  remaining: info.remaining,
                  limit: info.limit,
                  used: info.used,
                })}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-warning/30 bg-warning-light p-3 text-sm text-warning-ink">
          {isExactFit ? t('quotaCap.noteExact') : t('quotaCap.note', { dropped })}
        </div>

        {/* Muster des RED-UpgradeModal (C-21) — ergaenzt den Warn-Hinweis oben,
            ersetzt ihn nicht. */}
        {effectivePlan && price && (
          <PlanPriceCard
            plan={effectivePlan}
            price={price}
            showRecommendedBadge={!!(recommendedPlan && recommendedPlan.tier !== nextPlan?.tier)}
          />
        )}

        <DialogFooter className="gap-2 sm:flex-row sm:justify-between">
          <Button variant="ghost" onClick={onClose} className="sm:w-auto">
            {t('quotaCap.cancel')}
          </Button>
          <div className="flex flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={onProceed} className="sm:w-auto gap-2">
              <Sparkles className="h-4 w-4" />
              {isExactFit
                ? t('quotaCap.proceedExact', { remaining: info.remaining })
                : t('quotaCap.proceed', { remaining: info.remaining })}
            </Button>
            <Button
              onClick={() => { onClose(); navigate('/pricing'); }}
              className="sm:w-auto gap-1"
            >
              <ArrowUpRight className="h-4 w-4" />
              {/* Band: Name == Kapazitaets-Phrase — "1.000 Produkte ansehen
                  (1.000 Produkte)" waere gedoppelt, das Band nennt sich einmal. */}
              {effectivePlan
                ? isCapTier(effectivePlan.tier)
                  ? t('quotaCap.upgradeToBand', { plan: planDisplayName(effectivePlan, i18n?.language, t) })
                  : t('quotaCap.upgradeTo', { plan: effectivePlan.name, label: formatProductsPhrase(effectivePlan, i18n?.language, t) })
                : t('quotaCap.upgrade')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
